/**
 * TreeEnrichScanModal — tree-wide sweep of the shared Knowledge Graph. Lists
 * every historical person whose data or relatives other researchers already
 * recorded but this tree is missing, and lets the owner enrich them one by one
 * without hunting person-to-person.
 *
 * Each row opens the per-person EnrichFromKGModal; after applying, the row's
 * counts refresh in place. Portaled to body to clear the Tree page's map
 * stacking context.
 */
import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../services/api.js'
import EnrichFromKGModal from '../person/EnrichFromKGModal.jsx'
import './TreeEnrichScanModal.css'

export default function TreeEnrichScanModal({ treeId, onClose, onApplied }) {
  const [loading, setLoading] = useState(true)
  const [digest, setDigest] = useState(null)
  const [error, setError] = useState(null)
  const [active, setActive] = useState(null) // { person_id, name } being enriched
  const [batch, setBatch] = useState(null)   // { done, total } while "Enrich all" runs
  const [batchResult, setBatchResult] = useState(null)

  const runScan = useCallback(() => {
    setLoading(true)
    setError(null)
    api.get(`/trees/${treeId}/enrich-scan`)
      .then(setDigest)
      .catch(e => setError(e.message || 'Scan failed'))
      .finally(() => setLoading(false))
  }, [treeId])

  useEffect(() => { runScan() }, [runScan])

  const people = digest?.people || []

  // Apply every person's safe defaults in one pass: fills + refinements +
  // relatives. Genuine conflicts are left untouched (they need a human choice
  // per-person), and cross-canonical link candidates are skipped too.
  async function enrichAll() {
    setBatch({ done: 0, total: people.length })
    setBatchResult(null)
    let relatives = 0, fields = 0, conflicts = 0
    for (let i = 0; i < people.length; i++) {
      const p = people[i]
      try {
        const preview = await api.get(`/persons/${p.person_id}/enrich-preview?tree_id=${treeId}`)
        const fieldPayload = {}
        for (const row of preview.fields || []) {
          if (row.mode === 'conflict') conflicts++
          else fieldPayload[row.field] = row.proposed
        }
        const rels = (preview.relatives || []).map(r => ({
          target_person_id: r.target_person_id, relation: r.relation,
        }))
        if (Object.keys(fieldPayload).length || rels.length) {
          const res = await api.post(`/persons/${p.person_id}/enrich-apply`, {
            tree_id: treeId, fields: fieldPayload, relatives: rels,
          })
          relatives += res.relatives_added || 0
          fields += res.fields_applied || 0
        }
      } catch { /* skip this person, keep going */ }
      setBatch({ done: i + 1, total: people.length })
    }
    setBatch(null)
    setBatchResult({ relatives, fields, conflicts })
    onApplied?.()
    runScan()
  }

  return createPortal((
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal tree-enrich-modal" onClick={e => e.stopPropagation()}>
          <header className="modal__header">
            <h2>Enrich this tree from the Knowledge Graph</h2>
            <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
          </header>

          <div className="tree-enrich-modal__body">
            {loading && <div className="tree-enrich-modal__muted">Scanning your tree against the Knowledge Graph…</div>}
            {error && <div className="tree-enrich-modal__error">{error}</div>}

            {!loading && !error && people.length === 0 && (
              <div className="tree-enrich-modal__muted">
                Nothing to pull in right now. As other researchers record the
                same people, matches will show up here.
              </div>
            )}

            {!loading && !error && people.length > 0 && (
              <>
                <p className="tree-enrich-modal__intro">
                  <strong>{digest.total_relatives}</strong> {digest.total_relatives === 1 ? 'relative' : 'relatives'} and{' '}
                  <strong>{digest.total_fields}</strong> {digest.total_fields === 1 ? 'detail' : 'details'} across{' '}
                  <strong>{people.length}</strong> {people.length === 1 ? 'person' : 'people'} can be filled from other researchers' trees.
                </p>

                {/* Enrich-all: one click applies the safe defaults for everyone */}
                {batch ? (
                  <div className="tree-enrich-modal__batchbar">
                    Enriching {batch.done} / {batch.total}…
                  </div>
                ) : (
                  <div className="tree-enrich-modal__allrow">
                    <button className="btn btn-primary" onClick={enrichAll}>
                      ✨ Enrich all {people.length} →
                    </button>
                    <span className="tree-enrich-modal__allnote">
                      Adds missing relatives + fills empty/vaguer details. Conflicting values are left for you to review one-by-one.
                    </span>
                  </div>
                )}

                {batchResult && !batch && (
                  <div className="tree-enrich-modal__batchresult">
                    Added <strong>{batchResult.relatives}</strong> {batchResult.relatives === 1 ? 'relative' : 'relatives'} and
                    {' '}filled <strong>{batchResult.fields}</strong> {batchResult.fields === 1 ? 'detail' : 'details'}.
                    {batchResult.conflicts > 0 && ` ${batchResult.conflicts} conflicting ${batchResult.conflicts === 1 ? 'value was' : 'values were'} left for you to review below.`}
                  </div>
                )}

                <div className="tree-enrich-modal__list">
                  {people.map(p => (
                    <div key={p.person_id} className="tree-enrich-modal__row">
                      <div className="tree-enrich-modal__row-main">
                        <span className="tree-enrich-modal__name">{p.name}</span>
                        {p.birth_year && <span className="tree-enrich-modal__years">b. {p.birth_year}</span>}
                        <div className="tree-enrich-modal__counts">
                          {p.relative_count > 0 && (
                            <span className="tree-enrich-modal__count tree-enrich-modal__count--rel">
                              {p.relative_count} {p.relative_count === 1 ? 'relative' : 'relatives'}
                            </span>
                          )}
                          {p.field_count > 0 && (
                            <span className="tree-enrich-modal__count tree-enrich-modal__count--field">
                              {p.field_count} {p.field_count === 1 ? 'detail' : 'details'}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={!!batch}
                        onClick={() => setActive({ person_id: p.person_id, name: p.name })}
                      >
                        Enrich →
                      </button>
                    </div>
                  ))}
                </div>

                {digest.truncated && (
                  <div className="tree-enrich-modal__muted" style={{ marginTop: '0.75rem' }}>
                    Showing the first {digest.scanned} people — run again after enriching these to scan the rest.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {active && (
        <EnrichFromKGModal
          personId={active.person_id}
          treeId={treeId}
          personName={active.name}
          onClose={() => setActive(null)}
          onApplied={() => {
            onApplied?.()   // refresh the underlying tree view
            runScan()       // refresh the digest so this person's counts update
          }}
        />
      )}
    </>
  ), document.body)
}
